import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import InfiniteScroll from 'react-infinite-scroller'
import { useHistory } from 'react-router-dom'

import getUserAvailableExpenseTypes, {
  Expense,
  ExpenseTypeFilter,
  PagedExpenses,
} from 'core/application/expenses/getUserAvailableExpenseTypes'
import { ExpenseTypeEligibility } from 'core/domain/expenses/expenseTypeEligibility'

import { Button, Col, Collapse, CollapseItem, Icon, Row, Skeleton } from 'common/components'
import { useQueryRequestV2 } from 'common/hooks'
import { ROUTE_PATHS } from 'common/routePaths'

import ExpenseIcon from './common/ExpenseIcon'

import './ExpenseType.less'

interface ExpenseTypeListProps {
  expenseTypeFilters: ExpenseTypeFilter
  planPeriodId: string
  onPremiumItemPress?: () => void
  onRowSelected?: (expense: Expense) => void
  onSearchCompleted?: (totalItemCount) => void
}

const ExpenseTypeList: React.FC<ExpenseTypeListProps> = ({
  expenseTypeFilters,
  planPeriodId,
  onPremiumItemPress,
  onRowSelected,
  onSearchCompleted,
}) => {
  const history = useHistory()
  const { t: translate } = useTranslation('ee-dashboard')

  const [hasMoreExpenseTypes, setHasMoreExpenseTypes] = useState<boolean>(true)
  const [pagedExpenseTypes, setPagedExpenseTypes] = useState<PagedExpenses | null>(null)
  const [expenseTypesLoading, setExpenseTypesLoading] = useState<boolean>(false)
  const [currentQueueNumber, setCurrentQueueNumber] = useState<number>(1)
  const parentScrollDiv = useRef(null)

  const { isLoading: isExpenseTypesLoading, sendQuery: sendGetExpenseTypesRequest } = useQueryRequestV2({
    initialState: null,
  })

  useEffect(() => {
    ;(async () => {
      setExpenseTypesLoading(true)
      const result = await sendGetExpenseTypesRequest(() =>
        getUserAvailableExpenseTypes({
          ...expenseTypeFilters,
          pageNumber: 1,
        }),
      )
      onSearchCompleted(result.totalItemCount)
      setPagedExpenseTypes(result)
      setCurrentQueueNumber(1)
      setHasMoreExpenseTypes(result.pageCount > 1)
      setExpenseTypesLoading(false)
    })()
  }, [expenseTypeFilters])

  const onExpensesEndReach = async (pageNumber) => {
    if (!pagedExpenseTypes || isExpenseTypesLoading) return

    if (pagedExpenseTypes.pageCount < currentQueueNumber) return

    const nextPageNumber = currentQueueNumber + 1
    const result = await sendGetExpenseTypesRequest(() =>
      getUserAvailableExpenseTypes({
        ...expenseTypeFilters,
        pageNumber: nextPageNumber,
      }),
    )

    setPagedExpenseTypes({
      ...result,
      items: [...pagedExpenseTypes.items, ...result.items],
    })
    setCurrentQueueNumber(nextPageNumber)
    setHasMoreExpenseTypes(result.pageCount > nextPageNumber)
  }

  return (
    <>
      <Row className="expense_type_list_table_header">
        <Col xs={6} sm={4} md={2}>
          Eligible
        </Col>
        <Col xs={18} sm={20} md={22}>
          Type of expense
        </Col>
      </Row>
      <Skeleton loading={!pagedExpenseTypes}>
        {pagedExpenseTypes && (
          <>
            <div className={'expense_type_list_items'} ref={parentScrollDiv}>
              <InfiniteScroll
                pageStart={0}
                loadMore={onExpensesEndReach}
                hasMore={hasMoreExpenseTypes}
                useWindow={false}
                getScrollParent={() => parentScrollDiv.current}
              >
                {pagedExpenseTypes.items.map((item: Expense, index) => (
                  <Collapse
                    className="expense_type_list_collapse"
                    expandIconPosition="end"
                    expandIcon={({ isActive }) =>
                      isActive ? (
                        <Icon type="fa fa-arrow-down" fontAwesome />
                      ) : (
                        <Icon type="fa fa-arrow-right" fontAwesome />
                      )
                    }
                  >
                    <CollapseItem
                      className="expense_type_list"
                      key={item.code}
                      header={
                        <>
                          <Row className="expense_type_list_table_row">
                            <Col xs={6} sm={4} md={2} className="text-left">
                              <ExpenseIcon
                                expenseTypeId={item.eligibilityType}
                                isPremium={item.isPremium}
                              ></ExpenseIcon>
                            </Col>
                            <Col xs={18} sm={20} md={22}>
                              <p className="mb-0">{item.name}</p>
                            </Col>
                          </Row>
                        </>
                      }
                      content={
                        <Row>
                          <Col xs={2}></Col>
                          <Col xs={22}>
                            {item.isPremium && (
                              <>
                                <div>
                                  <p>
                                    For recurring insurance premiums, you only need to request reimbursement once per
                                    year (unless your premium changes).
                                    <br />
                                    When your request is verified, you’ll be set for the rest of the year!
                                  </p>
                                </div>
                                <div>
                                  <p>You’ll need a document showing:</p>
                                  <ul>
                                    <li>your insurance type</li>
                                    <li>your insurance coverage period</li>
                                    <li>how much you pay for your premium</li>
                                  </ul>
                                </div>
                              </>
                            )}
                            {!item.isPremium && item.eligibilityType !== 2 && (
                              <>
                                <div>
                                  <p>
                                    In order for us to verify your expenses, we need to be able to see the following
                                    information:
                                  </p>
                                  <ul>
                                    <li>The date of service</li>
                                    <li>The services done or item(s) purchased</li>
                                    <li>The cost to you</li>
                                  </ul>
                                </div>
                                <div>
                                  <p>Documentation that typically has all of that information:</p>
                                  <ul>
                                    <li>Explanation of Benefits (EOB)</li>
                                    <li>Itemized Receipt</li>
                                    <li>Invoice or Patient Ledger</li>
                                  </ul>
                                  <p>
                                    For more information, see the Documentation Requirements Medical Expenses article in
                                    our Help Center.
                                  </p>
                                </div>
                              </>
                            )}
                            {!item.isPremium && item.eligibilityType === 2 && (
                              <>
                                <div>
                                  <p>
                                    The IRS requires that some medical expense reimbursement requests include a
                                    physician's note recommending the item or service to treat a specific medical
                                    condition. This shows that the expense is primarily for medical care and is thus
                                    eligible for reimbursement.
                                  </p>
                                  <p>
                                    Your physician can write a note for this or use our Letter of Medical Necessity
                                    template and submit it along with your other documentation.
                                  </p>
                                  <p>
                                    For more information, see the Doctor’s Note Requirements article in our Help Center
                                  </p>
                                </div>
                              </>
                            )}
                            <div>
                              <Button
                                type="primary"
                                className="mt-16"
                                disabled={
                                  item.eligibilityType === ExpenseTypeEligibility.NotReimbursable ||
                                  item.eligibilityType === ExpenseTypeEligibility.HSANotEligible
                                }
                                onClick={() => {
                                  if (item.isPremium) {
                                    if (onPremiumItemPress && onRowSelected) {
                                      onPremiumItemPress()
                                      onRowSelected(item)
                                    }
                                  } else {
                                    history.push(
                                      `${ROUTE_PATHS.ELIGIBLE_EMPLOYEE_SUBMIT_EXPENSE}?expenseTypeId=${item.id}&planPeriodId=${planPeriodId}`,
                                    )
                                  }
                                }}
                              >
                                Get Reimbursed
                                <Icon type="fa fa-arrow-right" fontAwesome />
                              </Button>
                            </div>
                          </Col>
                        </Row>
                      }
                    />
                  </Collapse>
                ))}
              </InfiniteScroll>
            </div>
          </>
        )}
      </Skeleton>
    </>
  )
}

export default ExpenseTypeList
